/**
 * Premium "Partner Outro — & vieles mehr".
 *
 * Marketing-Use-Case: nach 1-3 `partnerServiceDetail`-Szenen brauche ich
 * ein starkes Ende, das suggeriert „das war nur ein Ausschnitt — viel mehr
 * davon findest du auf Allyver". Funktioniert branche-übergreifend (Händler,
 * Dienstleister, Restaurants, Hotels …), weil keine partnerspezifischen
 * Felder gerendert werden — nur das Versprechen + Allyver-Brand-CTA.
 *
 * Visuelles Konzept (von oben nach unten):
 *
 *          Und vieles                    (Hero-Line 1, ruhig, Mask-Reveal)
 *            MEHR                        (Hero-Line 2, Akzent-Gradient
 *                                         + Glow + Underline-Sweep)
 *
 *      wartet auf dich auf Allyver       (Subline)
 *
 *      ────────────                      (Divider)
 *
 *
 *         ╔════════════════╗
 *         ║                ║
 *         ║   ALLYVER      ║             (GROSSES Allyver-Logomark mit
 *         ║                ║              Akzent-Drop-Shadow — der Star)
 *         ╚════════════════╝
 *           www.allyver.com              (URL-Pill, kompakt)
 *
 *      ┌──────────────────────────┐
 *      │  JETZT ENTDECKEN   →     │     (CTA-Pill, Akzent-Gradient,
 *      └──────────────────────────┘      atmet sanft)
 *
 * Inputs:
 *   text      – Headline. Wird beim letzten Wort getrennt:
 *               Vorderteil ist normal weiß, das letzte Wort bekommt
 *               Akzent-Gradient + Glow. Default „und vieles mehr".
 *   subtext   – Subline unter dem Hero (default „wartet auf dich auf Allyver").
 *   number    – CTA-Label (default „Jetzt entdecken").
 *   url       – URL-Pill (default „www.allyver.com").
 *   accent    – Akzent (default primary).
 *   extras.sfx – Auto-SFX an/aus.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  resolveBrandLogoSrc, sceneSfxAudio, type SceneSfxBeat, typeSize, weights, withAlpha,
  colors as themeColors,
} from "../../reel/sceneShared.js";

export const partnerOutroMore: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const headlineRaw = (inputs.text ?? "Und vieles mehr").trim();
  const sub = (inputs.subtext ?? "wartet auf dich auf Allyver").trim();
  const cta = (inputs.number ?? "Jetzt entdecken").toString().trim();
  const url = (inputs.url ?? "www.allyver.com").trim();

  // Headline splitten am LETZTEN Leerzeichen → erstes Teil normal, letztes
  // Wort wird mit Akzent-Gradient gerendert.
  // „und vieles mehr" → vorder = „und vieles", accent = „mehr"
  // „mehr entdecken" → vorder = „mehr", accent = „entdecken"
  // Einzelnes Wort → komplett im Akzent.
  const lastSpace = headlineRaw.lastIndexOf(" ");
  const heroPre = lastSpace >= 0 ? headlineRaw.slice(0, lastSpace).trim() : "";
  const heroAccent = lastSpace >= 0 ? headlineRaw.slice(lastSpace + 1).trim() : headlineRaw;

  const logoSrc = resolveBrandLogoSrc();

  // ---- Typo-Sizes ---------------------------------------------------------
  // Hero ist bewusst ruhiger: Vorder- und Akzent-Zeile in ähnlicher Größe.
  // Das Allyver-Logo darunter ist der visuelle Star (groß), die Wortmarke
  // dient nur dem narrativen Bogen — der CTA bleibt dominant.
  const heroPreSize = typeSize(isVertical ? "xl" : "hero", ctx.format) * 0.7;
  const heroAccentSize = typeSize(isVertical ? "xl" : "hero", ctx.format) * 0.82;
  const subSize = typeSize("md", ctx.format) * 0.78;
  const urlSize = typeSize("sm", ctx.format) * 0.9;
  const ctaSize = typeSize("md", ctx.format) * 0.78;
  const logoH = isVertical ? 220 : 180;

  // ---- IDs ----------------------------------------------------------------
  const t = makeTl(clipStartSec);
  const padId = `${clipPrefix}-pad`;
  const heroPreWrapId = `${clipPrefix}-hero-pre-wrap`;
  const heroPreMaskId = `${clipPrefix}-hero-pre-mask`;
  const heroAccentWrapId = `${clipPrefix}-hero-acc-wrap`;
  const heroAccentMaskId = `${clipPrefix}-hero-acc-mask`;
  const heroAccentGlowId = `${clipPrefix}-hero-acc-glow`;
  const heroUnderlineId = `${clipPrefix}-hero-ul`;
  const subId = `${clipPrefix}-sub`;
  const dividerId = `${clipPrefix}-div`;
  const logoId = `${clipPrefix}-logo`;
  const urlId = `${clipPrefix}-url`;
  const ctaWrapId = `${clipPrefix}-cta-wrap`;
  const ctaId = `${clipPrefix}-cta`;
  const ctaGlowId = `${clipPrefix}-cta-glow`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  // ---- Hero (zwei Zeilen, Mask-Reveal) ------------------------------------
  const maskGradient = `linear-gradient(180deg, ${withAlpha(accent, 0)} 0%, ${accent} 60%, ${withAlpha(accent, 0)} 100%)`;
  const heroPreHtml = heroPre
    ? `<div id="${heroPreWrapId}" style="position:relative;display:inline-block;padding:4px 12px;">
        <div style="position:relative;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${heroPreSize}px;line-height:0.95;letter-spacing:-1.8px;color:${themeColors.fg};text-align:center;">${escapeHtml(heroPre.toUpperCase())}</div>
        <div id="${heroPreMaskId}" style="position:absolute;inset:0;background:${maskGradient};pointer-events:none;transform-origin:50% 0%;"></div>
      </div>`
    : "";

  const heroAccentHtml = `<div id="${heroAccentWrapId}" style="position:relative;display:inline-block;padding:6px 18px;">
    <div id="${heroAccentGlowId}" style="position:absolute;inset:-18px;border-radius:32px;background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.55)} 0%, transparent 70%);filter:blur(22px);pointer-events:none;opacity:0;"></div>
    <div style="position:relative;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${heroAccentSize}px;line-height:0.92;letter-spacing:-3px;background:linear-gradient(180deg, ${themeColors.fg} 0%, ${themeColors.fg} 35%, ${accent} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;text-shadow:0 0 60px ${withAlpha(accent, 0.55)};">${escapeHtml(heroAccent.toUpperCase())}</div>
    <div id="${heroUnderlineId}" style="position:absolute;left:18px;right:18px;bottom:-2px;height:4px;border-radius:9999px;background:linear-gradient(90deg, ${accent} 0%, ${withAlpha(accent, 0.6)} 100%);transform-origin:0% 50%;box-shadow:0 0 16px ${withAlpha(accent, 0.7)};"></div>
    <div id="${heroAccentMaskId}" style="position:absolute;inset:0;background:${maskGradient};pointer-events:none;transform-origin:50% 0%;"></div>
  </div>`;

  // ---- Subline -----------------------------------------------------------
  const subHtml = sub
    ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;line-height:1.4;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;text-wrap:balance;max-width:${isVertical ? 720 : 1080}px;">${escapeHtml(sub)}</div>`
    : "";

  // ---- Brand-Block (Logo + URL-Pill) -------------------------------------
  // Allyver-Logo ist der visuelle Star: groß (220px in 9:16) + sanfter
  // Accent-Drop-Shadow, damit es vom Backdrop „abhebt" ohne aufdringlich
  // zu wirken.
  const logoHtml = logoSrc
    ? `<img id="${logoId}" src="${escapeHtml(logoSrc)}" alt="Allyver" style="height:${logoH}px;width:auto;object-fit:contain;filter:drop-shadow(0 16px 40px ${withAlpha(accent, 0.45)});" />`
    : `<div id="${logoId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${logoH * 0.55}px;letter-spacing:-2px;color:${themeColors.fg};text-shadow:0 16px 40px ${withAlpha(accent, 0.45)};">Allyver</div>`;

  const urlHtml = url
    ? `<div id="${urlId}" style="display:inline-flex;align-items:center;gap:10px;padding:8px 18px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.06)};border:1px solid ${withAlpha(themeColors.fg, 0.18)};font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${urlSize}px;letter-spacing:1.2px;color:${withAlpha(themeColors.fg, 0.85)};">
        <svg width="${Math.round(urlSize * 1.05)}" height="${Math.round(urlSize * 1.05)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
        <span>${escapeHtml(url)}</span>
      </div>`
    : "";

  // ---- CTA-Pill ----------------------------------------------------------
  const ctaHtml = `<div id="${ctaWrapId}" style="position:relative;display:inline-flex;align-items:center;justify-content:center;">
    <div id="${ctaGlowId}" style="position:absolute;left:50%;top:50%;width:140%;height:200%;transform:translate(-50%,-50%);background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.6)} 0%, transparent 70%);filter:blur(10px);pointer-events:none;opacity:0;"></div>
    <div id="${ctaId}" style="position:relative;display:inline-flex;align-items:center;gap:16px;padding:${isVertical ? 22 : 18}px ${isVertical ? 46 : 38}px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 18px 50px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:1.5px;text-transform:uppercase;color:${themeColors.bg};">
      <span>${escapeHtml(cta)}</span>
      <svg width="${Math.round(ctaSize * 1.05)}" height="${Math.round(ctaSize * 1.05)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
    </div>
  </div>`;

  // ---- Trennlinie zwischen Hero & Brand-Block ----------------------------
  const dividerHtml = `<div id="${dividerId}" style="width:${isVertical ? 240 : 320}px;max-width:60%;height:1px;background:linear-gradient(90deg, transparent 0%, ${withAlpha(themeColors.fg, 0.35)} 50%, transparent 100%);transform-origin:50% 50%;"></div>`;

  // ---- Choreografie ------------------------------------------------------
  // ~2.2 s Aufbau, danach Logo + CTA atmen bis Outro
  const beatHeroPre = 0.15;
  const beatHeroAccent = 0.50;     // der Hero-Moment
  const beatHeroUnderline = 0.95;
  const beatSub = 1.10;
  const beatDivider = 1.30;
  const beatLogo = 1.45;            // großes Allyver-Logo poppt rein
  const beatUrl = 1.80;
  const beatCta = 2.05;

  // ---- Init --------------------------------------------------------------
  const init = [
    heroPreHtml ? gsapSetLine(`#${heroPreWrapId}`, { opacity: 0 }) : "",
    heroPreHtml ? gsapSetLine(`#${heroPreMaskId}`, { scaleY: 1, opacity: 1 }) : "",
    gsapSetLine(`#${heroAccentWrapId}`, { opacity: 0 }),
    gsapSetLine(`#${heroAccentMaskId}`, { scaleY: 1, opacity: 1 }),
    gsapSetLine(`#${heroAccentGlowId}`, { opacity: 0, scale: 0.7 }),
    gsapSetLine(`#${heroUnderlineId}`, { scaleX: 0, opacity: 0 }),
    subHtml ? gsapSetLine(`#${subId}`, { opacity: 0, y: 14 }) : "",
    gsapSetLine(`#${dividerId}`, { opacity: 0, scaleX: 0 }),
    gsapSetLine(`#${logoId}`, { opacity: 0, scale: 0.7 }),
    urlHtml ? gsapSetLine(`#${urlId}`, { opacity: 0, y: 10 }) : "",
    gsapSetLine(`#${ctaWrapId}`, { opacity: 0, y: 18, scale: 0.92 }),
    gsapSetLine(`#${ctaGlowId}`, { opacity: 0, scale: 0.6 }),
  ].filter(Boolean).join("\n");

  // ---- Tweens ------------------------------------------------------------
  const tweens: string[] = [];

  if (heroPreHtml) {
    tweens.push(
      t.to(`#${heroPreWrapId}`, { opacity: 1, duration: 0.1, ease: 'raw:"none"' }, beatHeroPre),
      t.to(`#${heroPreMaskId}`, { scaleY: 0, transformOrigin: '50% 100%', duration: 0.6, ease: 'raw:"power3.inOut"' }, beatHeroPre),
    );
  }

  tweens.push(
    t.to(`#${heroAccentWrapId}`, { opacity: 1, duration: 0.1, ease: 'raw:"none"' }, beatHeroAccent),
    t.to(`#${heroAccentMaskId}`, { scaleY: 0, transformOrigin: '50% 100%', duration: 0.75, ease: 'raw:"power3.inOut"' }, beatHeroAccent),
    t.fromTo(`#${heroAccentGlowId}`, { opacity: 0, scale: 0.7 }, { opacity: 1, scale: 1, duration: 0.7, ease: 'raw:"power2.out"' }, beatHeroAccent + 0.15),
    t.to(`#${heroUnderlineId}`, { scaleX: 1, opacity: 1, duration: 0.55, ease: 'raw:"power3.out"' }, beatHeroUnderline),
  );

  if (subHtml) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: 'raw:"power3.out"' }, beatSub));
  tweens.push(t.to(`#${dividerId}`, { opacity: 1, scaleX: 1, duration: 0.5, ease: 'raw:"power3.out"' }, beatDivider));
  // Großes Allyver-Logo: kommt mit kräftigem Back-Out-Pop rein, das ist der
  // visuelle Star der Szene.
  tweens.push(t.to(`#${logoId}`, { opacity: 1, scale: 1, duration: 0.7, ease: 'raw:"back.out(1.7)"' }, beatLogo));
  if (urlHtml) tweens.push(t.to(`#${urlId}`, { opacity: 1, y: 0, duration: 0.5, ease: 'raw:"power3.out"' }, beatUrl));

  tweens.push(
    t.to(`#${ctaWrapId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: 'raw:"back.out(1.6)"' }, beatCta),
    t.fromTo(`#${ctaGlowId}`, { opacity: 0, scale: 0.6 }, { opacity: 1, scale: 1, duration: 0.6, ease: 'raw:"power2.out"' }, beatCta + 0.1),
  );

  // CTA atmet sanft bis kurz vor dem Outro
  const breathStart = beatCta + 0.7;
  const breathDur = Math.max(0.9, clipDurationSec - breathStart - 0.6);
  tweens.push(
    t.to(`#${ctaId}`, { scale: 1.035, duration: breathDur / 2, repeat: -1, yoyo: true, ease: 'raw:"sine.inOut"' }, breathStart),
    t.to(`#${ctaGlowId}`, { opacity: 0.6, duration: breathDur / 2, repeat: -1, yoyo: true, ease: 'raw:"sine.inOut"' }, breathStart),
    t.to(`#${heroAccentGlowId}`, { opacity: 0.5, duration: breathDur / 2, repeat: -1, yoyo: true, ease: 'raw:"sine.inOut"' }, breathStart),
  );

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12));

  // ---- SFX (dezent, marketing-spitz) -------------------------------------
  const sfxBeats: SceneSfxBeat[] = [
    ...(heroPreHtml ? [{ sound: "swoosh" as const, atSec: beatHeroPre + 0.02, volume: 0.18, durSec: 0.5 }] : []),
    { sound: "swoosh", atSec: beatHeroAccent + 0.02, volume: 0.22, durSec: 0.7 },
    { sound: "ding", atSec: beatHeroAccent + 0.22, volume: 0.24, durSec: 0.8 },
    { sound: "shimmer", atSec: beatHeroUnderline + 0.02, volume: 0.14, durSec: 0.6 },
    { sound: "tick", atSec: beatDivider + 0.02, volume: 0.10, durSec: 0.25 },
    // Logo ist jetzt visuell der Star → eigener pop-Beat (lauter als der Hero).
    { sound: "pop", atSec: beatLogo + 0.05, volume: 0.28, durSec: 0.55 },
    { sound: "pop", atSec: beatCta + 0.05, volume: 0.26, durSec: 0.55 },
  ];
  const sfxHtml = sceneSfxAudio({ clipPrefix, clipStartSec, inputs, beats: sfxBeats });

  // ---- DOM ---------------------------------------------------------------
  const padTopBot = isVertical ? 110 : 80;
  const padX = isVertical ? 60 : 100;
  const heroBlock = `<div style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 6 : 4}px;">
    ${heroPreHtml}
    ${heroAccentHtml}
  </div>`;

  const brandBlock = `<div style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 14 : 12}px;">
    ${logoHtml}
    ${urlHtml}
  </div>`;

  const html = `<div id="${padId}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 22 : 18}px;padding:${padTopBot}px ${padX}px;text-align:center;">
    ${heroBlock}
    ${subHtml}
    ${dividerHtml}
    ${brandBlock}
    ${ctaHtml}
  </div>${sfxHtml ? `\n${sfxHtml}` : ""}`;

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
